import { nvstore } from "../state/store";
import { actionTypes } from "../state/actions/cartActions";
import { StorageUtils } from "../utils/storage.utils";
import { DOMUtils } from "../utils/dom.utils";
import { getLocaleConfig } from "../utils/locale.utils";
import CartService, { ApiAccess } from "../services/ApiAcccess.service";
import nvCartTranslations from "../translations/nvCartTranslations";
import { nvCartHelpers } from "../helpers/cart-helpers";
import { ProductModule } from "../product/product.module";
import { cartEnabledLocales } from "../cart/cartLocales";
import { getHandlebarsRuntime, registerHandlebarsHelpers } from "../helpers/handlebars-helpers";
import { waitForProductElements, scanProducts } from "../product/product.events";
import { ProductContext } from "../product/product.context";

export const initCart = async (options: any, cartKey: string, isHeadless: boolean) => {
  let activeTab = 'nvidia';

  // Store nvLocale globally for access by reducer
  (window as any).nvLocale = options.locale;

  // ✅ Initialize locale configuration FIRST (needed by CartService)
  if (options == null) {
    console.log("Marketplace: You must provide options for locale and mountId");
  }
  if (options.locale == null) {
    console.log("Marketplace: You must set an option for locale");
  }
  if (options.mountId == null) {
    console.log("Marketplace: You must set an option for mountId");
  }
  const locale: string = options.locale;

  // Get locale configuration
  const localeConfig = getLocaleConfig(options.locale);
  const { DRlocale, currency, apiCurrency, region, PromoLocale } = localeConfig;

  // Assign locale config to options
  options['cartKey'] = cartKey;
  options['currency'] = currency;
  options['apiCurrency'] = apiCurrency;
  options['locale'] = options.locale;
  options['region'] = region;
  options['PromoLocale'] = PromoLocale;
  const DROptions: any = options;

  // ✅ Initialize CartService BEFORE scanProducts (so getProducts is available)
  CartService.initialize(
    { locale: DRlocale, currency: apiCurrency, environment: options.environment },
    locale,
    apiCurrency,
    PromoLocale,
    isHeadless,
    actionTypes,
    nvstore
  );

  // 🔄 Scan products to populate cartProductsList before restoring cart
  console.log('🔍 Waiting for product elements to scan...');
  try {
    const buttonWait: NodeListOf<HTMLElement> = DOMUtils.querySelectorAll('.cta-button,.js-add-button');
    if (buttonWait) {
      buttonWait.forEach((el: HTMLElement) => {
        el.classList.add('bc-spinner-bg');
      });
    }
    await waitForProductElements(20, 300); // Wait up to 6 seconds for products
    console.log('✅ Product elements found, scanning products...');
    await scanProducts(options, isHeadless);
    const productsList = ProductContext.getCartProductsList();
    console.log('✅ Products scanned and loaded into context:', productsList.length, 'products');
    if (productsList.length > 0) {
      console.log('📦 Sample product:', productsList[0]);
    }
  } catch (error) {
    console.warn('⚠️ Could not scan products before cart restoration:', error);
    console.log('ℹ️ Proceeding with cart restoration anyway...');
  }

  // Check products are available before decompressing cart
  const productsBeforeDecompress = ProductContext.getCartProductsList();
  console.log('📊 Products available before getCookie:', productsBeforeDecompress.length);

  // Fetch existing cart from cookie AFTER scanning products
  let storedCartState = StorageUtils.getCookie(cartKey);
  console.log('🍪 Initial cart from cookie:', storedCartState);

  // If decompression returned cart with _needsProductDetails flag, try to get full details
  if (storedCartState && storedCartState._needsProductDetails && storedCartState.cart && storedCartState.cart.length > 0) {
    console.log('⚠️ Cart items need full product details');
    const availableProducts = ProductContext.getCartProductsList();

    if (availableProducts.length > 0) {
      console.log('📦 Products available, refreshing cart with full details...');
      // Re-fetch cookie to get full product details now that products are available
      storedCartState = StorageUtils.getCookie(cartKey);
      console.log('✅ Cart refreshed with full product details');
    } else {
      console.log('⚠️ Products not yet available, cart will have basic info only');
      // Cart will be updated later when products are scanned via ProductModule.setProducts
    }
  }

  // Restore cart to Redux store if it exists
  if (storedCartState && storedCartState.cart && Array.isArray(storedCartState.cart)) {
    if (storedCartState.cart.length > 0) {
      nvstore.dispatch({
        type: actionTypes.SET_CART,
        cart: storedCartState.cart,
        lastAction: null
      });
      console.log('✅ Cart restored to Redux from cookie:', storedCartState.cart.length, 'items');
    } else {
      console.log('ℹ️ Cart cookie exists but cart array is empty');
    }
  }

  // Check locale cookie
  const cookieLocale = StorageUtils.getCookie("nv_co");

  // Only clear cart if locale cookie exists AND locale has changed
  // Normalize locales to lowercase for comparison to avoid case-sensitivity issues
  const currentLocale = (options.locale || '').toLowerCase().replace('_', '-');
  const storedLocale = cookieLocale?.locale ? (cookieLocale.locale || '').toLowerCase().replace('_', '-') : null;

  if (storedLocale && storedLocale !== currentLocale) {
    console.log('🌍 Locale changed from', cookieLocale.locale, 'to', options.locale, '- clearing cart');
    console.log('  Normalized:', storedLocale, '→', currentLocale);
    StorageUtils.setCartData(cartKey, JSON.stringify({ cart: [], lastAction: null }));
    StorageUtils.setCookie("nv_co", JSON.stringify({ locale: options.locale }));
    // Clear Redux state too
    nvstore.dispatch({
      type: actionTypes.SET_CART,
      cart: [],
      lastAction: null
    });
  } else {
    // Set or update locale cookie (doesn't affect cart)
    if (storedLocale && storedLocale === currentLocale) {
      console.log('🌍 Locale unchanged:', options.locale);
    } else {
      console.log('🌍 Setting initial locale cookie:', options.locale);
    }
    StorageUtils.setCookie("nv_co", JSON.stringify({ locale: options.locale }));
  }

  // var getState = function () {
  //   var storedState = getCartData(cartKey);
  //   var state = { 'cart': [] };
  //   if (storedState) {
  //     state = storedState;//JSON.parse(storedState);

  //   }
  //   return state;
  // }
  // window.nvstore = {
  //   getState: getState
  // };

  var cartEle = DOMUtils.querySelector("#nv-cartTemplate");
  const source = cartEle?.innerHTML;
  const cartHeight = window.innerHeight - 315;

  // Register Handlebars helpers with apiCurrency and locale
  // categorizedProducts will be passed dynamically when rendering
  registerHandlebarsHelpers(apiCurrency, undefined, locale);

  const handlebarsRuntime = getHandlebarsRuntime();
  if (!handlebarsRuntime || typeof handlebarsRuntime.compile !== 'function') {
    console.error('[nvCart] Handlebars runtime unavailable. Cart initialization aborted.');
    return;
  }
  if (!source) {
    console.error('[nvCart] Cart template source unavailable. Cart initialization aborted.');
    return;
  }
  const cartTemplate = handlebarsRuntime.compile(source);

  // Helper function to get all cart elements (desktop and mobile)
  const getAllCartElements = (): HTMLElement[] => {
    const carts: HTMLElement[] = [];
    const desktopCart = DOMUtils.querySelector('.desktop-nav .cart_white.cart') ||
      DOMUtils.querySelector('.desktop-nav .cart_white') ||
      DOMUtils.querySelector('.desktop-nav .cart');
    const mobileCart = DOMUtils.querySelector('.mobile-nav .cart_white.cart') ||
      DOMUtils.querySelector('.mobile-nav .cart_white') ||
      DOMUtils.querySelector('.mobile-nav .cart');

    if (desktopCart) carts.push(desktopCart as HTMLElement);
    if (mobileCart) carts.push(mobileCart as HTMLElement);

    // Fallback: if no desktop/mobile specific carts found, try generic selectors
    if (carts.length === 0) {
      const genericCart = DOMUtils.querySelector('.cart_white.cart') ||
        DOMUtils.querySelector('.cart_white') ||
        DOMUtils.querySelector('.cart');
      if (genericCart) carts.push(genericCart as HTMLElement);
    }

    return carts;
  };

  // Helper function to get the currently visible cart element based on viewport
  const getCartElement = (): HTMLElement | null => {
    const allCarts = getAllCartElements();

    if (allCarts.length === 0) return null;
    if (allCarts.length === 1) return allCarts[0];

    // If multiple carts exist, determine which one is visible based on viewport
    const viewportWidth = window.innerWidth;
    const isMobile = viewportWidth <= 1023;

    // Check which cart is actually visible/rendered
    for (const cart of allCarts) {
      const parent = cart.closest('.desktop-nav, .mobile-nav');
      if (isMobile && parent?.classList.contains('mobile-nav')) {
        return cart;
      }
      if (!isMobile && parent?.classList.contains('desktop-nav')) {
        return cart;
      }
    }

    // Fallback: return the first one
    return allCarts[0];
  };

  // Helper function to apply class to all cart elements (for cases where both need updating)
  const applyToAllCarts = (callback: (cart: HTMLElement) => void): void => {
    const allCarts = getAllCartElements();
    allCarts.forEach(callback);
  };

  // var storedSession;
  // var time = new Date();
  // time = time.toUTCString();
  // var preState = getState();
  var DRCartData;
  var processCart = function (cartItems, category, partnerCart, state, callAddLineItems = false, S_ID = null) {
    var digiproduct = cartItems.filter(function (product) {
      return (product.productType == 'DOWNLOAD' || product.productType == 'DIGITAL');
    });
    var hwproduct = cartItems.filter(function (product) {
      return product.productType == 'PHYSICAL';
    });
    var hwgiftcard = cartItems.filter(function (product) {
      return product.productType == 'PHYSICALGIFTCARD';
    });
    var hwandphysicalgiftcard = cartItems.filter(function (product) {
      return product.productType != 'DOWNLOAD';
    });
    console.log("cartItems==>", cartItems);

    var updatedCart = cartItems;

    if (cartItems.length > 1) {
      if (digiproduct.length > 0 && hwandphysicalgiftcard.length > 0) {
        if (digiproduct.length > 0 && hwproduct.length > 0 && hwgiftcard.length > 0) {
          updatedCart = hwandphysicalgiftcard;
        } else if (digiproduct.length > 0 && hwproduct.length > 0) {
          updatedCart = hwproduct;
        } else if (digiproduct.length > 0 && hwgiftcard.length > 0) {
          if (cartItems[0].productType == 'DOWNLOAD' || cartItems[0].productType == 'DIGITAL') {
            updatedCart = digiproduct;
          } else {
            updatedCart = hwgiftcard;
          }
        }
      } else {
        updatedCart = cartItems;
      }
    } else {
      updatedCart = cartItems;
    }
    console.log("updatedCart==>", updatedCart);

    // Combine updatedCart and partnerCart with the entire state.cart to maintain all objects
    if (partnerCart && partnerCart.length) {
      // Iterate over partnerCart
      for (var i = 0; i < partnerCart.length; i++) {
        var partnerItem = partnerCart[i];
        var itemExists = false;

        // Iterate over updatedCart to check for matching ids
        for (var j = 0; j < updatedCart.length; j++) {
          if (updatedCart[j].id === partnerItem.id) {
            // If a matching id is found, update the existing object
            updatedCart[j] = partnerItem;
            itemExists = true;
            break;
          }
        }

        // If no matching id is found, add the new object to updatedCart
        if (!itemExists) {
          updatedCart.push(partnerItem);
        }
      }
    }
    console.log("updatedCart with partner==>", updatedCart);

    console.log("updatedCart==>", updatedCart);

    nvstore.dispatch({
      type: actionTypes.SET_CART,
      cart: updatedCart,
      lastAction: null
    });

    // Pass only the filtered cart items that match the category
    let checkoutCart = updatedCart.filter(item => item.category === category);

    if (callAddLineItems && S_ID) {
      CartService.addLineItems({
        products: checkoutCart,
        token: S_ID,
        challenge: StorageUtils.getSessionItem('checkoutClicked') as boolean,
        category: category,
        isDigital: digiproduct.length === cartItems.length
      });
    } else {
      CartService.checkout({ items: checkoutCart, category: category, isDigital: digiproduct.length === cartItems.length });
    }
  };
  nvstore.subscribe(function () {
    var state = nvstore.getState();
    var savedTab = sessionStorage.getItem('category');

    // Group products by category FIRST to determine available categories
    let categorizedProducts: any = {};
    state?.cart?.forEach(function (product) {
      if (!categorizedProducts[product.category]) {
        categorizedProducts[product.category] = [];
      }
      categorizedProducts[product.category].push(product);
    });

    // Determine activeTab based on priority:
    // 1. Saved tab from sessionStorage
    // 2. lastAction category
    // 3. If cart has items, use the category of the first item or most common category
    // 4. Fallback to 'nvidia'
    if (savedTab && categorizedProducts[savedTab]) {
      activeTab = savedTab;
    } else if (state?.lastAction?.category && categorizedProducts[state.lastAction.category]) {
      activeTab = state.lastAction.category;
    } else if (state?.cart?.length > 0) {
      // Use the category of the first item, or most common category if multiple exist
      if (state.cart.length === 1) {
        activeTab = state.cart[0].category;
      } else {
        // Find the category with the most items
        let maxCount = 0;
        let mostCommonCategory = 'nvidia';
        Object.keys(categorizedProducts).forEach(category => {
          if (categorizedProducts[category].length > maxCount) {
            maxCount = categorizedProducts[category].length;
            mostCommonCategory = category;
          }
        });
        activeTab = mostCommonCategory;
      }
    } else {
      activeTab = savedTab || state.activeTab || 'nvidia';
    }

    var obj = state.cart;
    var digiproduct, hwproduct;

    //Re-render cart to update
    var digiproduct, hwproduct, hwgiftcard, hwandphysicalgiftcard;

    // Re-register helpers with updated categorizedProducts for this render cycle
    registerHandlebarsHelpers(apiCurrency, categorizedProducts, locale);

    // Calculate active tab cart BEFORE rendering to ensure consistency
    var activeTabCart = state.cart.filter(product => product.category === activeTab);

    // Prepare the data object for Handlebars
    // Use activeTabCart to ensure rendered products match the calculated totals
    var renderedHtml = cartTemplate({
      height: cartHeight,
      activeTab: state?.lastAction?.category ? state?.lastAction?.category : activeTab,
      products: activeTabCart,
      currency: options.currency,
      categories: Object.keys(categorizedProducts),
      checkout: nvCartTranslations.translations.checkOut[locale],
      included: nvCartTranslations.translations.included[locale],
      cartIsEmpty: nvCartTranslations.translations.cartIsEmpty[locale]
    });
    // Update cart items in BOTH desktop and mobile navs
    const cartItemsElements = DOMUtils.querySelectorAll('.nv__cart__items');
    cartItemsElements.forEach(element => {
      element.innerHTML = renderedHtml;
    });
    console.log('✅ Updated', cartItemsElements.length, 'cart items element(s)');

    // Calculate cart total based on active tab category
    var totalPrice;

    if (activeTabCart?.length) {
      totalPrice = nvCartHelpers.getCartTotal(activeTabCart);
    } else {
      totalPrice = 0;
    }

    // Update cart total in BOTH desktop and mobile navs
    const cartTotalElements = DOMUtils.querySelectorAll('.cart__total');
    const totalPriceHtml = nvCartHelpers.localisePriceDisplay(options.apiCurrency, totalPrice);
    cartTotalElements.forEach(element => {
      element.innerHTML = totalPriceHtml;
    });
    console.log('✅ Updated', cartTotalElements.length, 'cart total element(s)');

    // Calculate total product count based on ALL items in cart (not just active tab)
    // The cart counter should show the total count across all categories
    var totalProductCount = nvCartHelpers.getTotalProductCount(state.cart || []);
    if (categorizedProducts && Object.keys(categorizedProducts).length > 1) {
      activeTab = state.lastAction?.category ? state.lastAction?.category : 'nvidia';
      var tabLinkEle = DOMUtils.getElementsByClassName(activeTab);
      for (var i = 0; i < tabLinkEle.length; i++) {
        (tabLinkEle[i] as HTMLElement).click();
      }
    } else {
      const tabContent = DOMUtils.querySelector('.tabcontent') as HTMLElement;
      if (tabContent) tabContent.classList.add('active');
    }

    // Always update cart counter in BOTH desktop and mobile navs (always visible, show 0 if empty)
    const cartCounters = DOMUtils.querySelectorAll('.cart-counter');
    const counterValue = totalProductCount > 0 ? totalProductCount.toString() : '0';
    cartCounters.forEach(counter => {
      (counter as HTMLElement).style.display = 'flex'; // Always visible
      counter.innerHTML = counterValue;
    });
    console.log('✅ Updated', cartCounters.length, 'cart counter(s) with value:', counterValue);

    if (state?.cart && state?.cart?.length) {
      digiproduct = activeTabCart.filter(function (product) {
        return product.productType == 'DOWNLOAD';
      });
      hwproduct = activeTabCart.filter(function (product) {
        return product.productType == 'PHYSICAL';
      });
      hwgiftcard = activeTabCart.filter(function (product) {
        return product.productType == 'PHYSICALGIFTCARD';
      });
      hwandphysicalgiftcard = activeTabCart.filter(function (product) {
        return product.productType != 'DOWNLOAD';
      });
      const cartUpdates = DOMUtils.querySelector(".cart__updates");
      const mixedCartUpdates = DOMUtils.querySelector(".mixedCart__updates");

      if (cartUpdates) (cartUpdates as HTMLElement).style.display = 'none';
      if (mixedCartUpdates) (mixedCartUpdates as HTMLElement).style.display = 'none';

      if (activeTabCart?.length > 1 && activeTab !== 'partner') {
        if (digiproduct.length > 0 && hwandphysicalgiftcard.length > 0) {
          // Purging Digital Product and adding hardware only
          if (digiproduct.length > 0 && hwproduct.length > 0 && hwgiftcard.length > 0) {
            if (cartUpdates) (cartUpdates as HTMLElement).style.display = 'block';
          } else if (digiproduct.length > 0 && hwproduct.length > 0) {
            if (cartUpdates) (cartUpdates as HTMLElement).style.display = 'block';
          } else if (digiproduct.length > 0 && hwgiftcard.length > 0) {
            if (mixedCartUpdates) (mixedCartUpdates as HTMLElement).style.display = 'block';
          }
        } else {
          if (cartUpdates) (cartUpdates as HTMLElement).style.display = 'none';
          if (mixedCartUpdates) (mixedCartUpdates as HTMLElement).style.display = 'none';
        }
      } else {
        if (cartUpdates) (cartUpdates as HTMLElement).style.display = 'none';
        if (mixedCartUpdates) (mixedCartUpdates as HTMLElement).style.display = 'none';
      }
      //Check if we need to open the cart or not
      // Only open cart on user actions, NOT on initial page load restore
      // Check if this is a user-initiated action (not a restore from cookie)
      const isUserAction = state.lastAction != null && (
        state.lastAction.type == actionTypes.ADD_ITEM_TO_CART ||
        state.lastAction.type == actionTypes.UPDATE_PRODUCTS ||
        state.lastAction.type == actionTypes.INC_ITEM_QUANTITY ||
        state.lastAction.type == actionTypes.DEC_ITEM_QUANTITY
      );

      // Open cart on user actions (but not on initial page load restore)
      if (isUserAction) {
        const cart = getCartElement();
        console.log('🛒 Opening cart - cart:', !!cart, 'viewport:', window.innerWidth, 'action:', state.lastAction?.type);
        if (cart) {
          cart.classList.add('cart--shown');
          console.log('✅ cart--shown class added');
        } else {
          console.warn('⚠️ cart element not found');
        }
        //Custom event could be dispatched here if needed
      }
    }
    //Save new cart state
    StorageUtils.setCookie(cartKey, JSON.stringify(state));
  });

  var spinnerHTML =
    '<div class="overlay"><div class="center-ovelay"><div class="swg-spinner-wrapper"><div class="swg-spinner"></div></div></div></div><style>.overlay{display: none;position: fixed;width: 100%;height: 100%;top: 0;left: 0;z-index: 9999;background-color: rgba(0, 0, 0, .5);}.overlay .center-ovelay {position: fixed;left: 0;top: 0;bottom: 0;right: 0;width: 100%;height: 100%;background-color: rgba(0, 0, 0, .5);z-index: 1105;display: block;}.swg-spinner-wrapper{position: absolute;top: 50%;left: 50%;display: flex;justify-content: center;align-items: center;margin: -1.5em;}.swg-spinner {border-radius: 50%;width: 3em;height: 3em;position: relative;border: 0.4em solid #76b900;border-left-color: transparent;-webkit-animation: load8 1.1s infinite linear;animation: load8 1.1s infinite linear;}body.loading{overflow: hidden;}body.loading .overlay{display: block;}@-webkit-keyframes load8{0%{-webkit-transform:rotate(0);-moz-transform:rotate(0);transform:rotate(0);-ms-transform:rotate(0);-o-transform:rotate(0)}100%{-webkit-transform:rotate(360deg);transform:rotate(360deg);-moz-transform:rotate(360deg);-ms-transform:rotate(360deg);-o-transform:rotate(360deg)}}@keyframes load8{0%{-webkit-transform:rotate(0);transform:rotate(0);-moz-transform:rotate(0);-ms-transform:rotate(0);-o-transform:rotate(0)}100%{-webkit-transform:rotate(360deg);transform:rotate(360deg);-moz-transform:rotate(360deg);-ms-transform:rotate(360deg);-o-transform:rotate(360deg)}}.js-add-button{visibility:hidden}.bc-spinner-bg{background-image:url(../assets/images/bouncing-circles.svg)!important;background-size:15%;background-repeat:no-repeat;background-position:center;height:auto;width:auto}.bc-spinner-bg.cta-button{visibility:hidden}:not(.bc-spinner-bg).cta-button{visibility:visible!important}}</style>';
  DOMUtils.insertAdjacentHTML(document.body, 'beforeend', spinnerHTML);
  var token = StorageUtils.getCookie("accessToken");
  //UI Bindings
  //If there is no digital river cart to fetch, then load the state from local storage

  var storedState = StorageUtils.getCookie(cartKey); //localStorage.getItem(cartKey);
  console.log('storedState==>', storedState);
  if (storedState === null || storedState === undefined || storedState == "") {
    storedState = { "cart": [], "lastAction": null };
    StorageUtils.setCookie(cartKey, JSON.stringify(storedState));
  }
  var now = new Date();
  var hasExpired = function (now, expireDate) {
    return (expireDate < now);
  }
  if (storedState != null) {
    var parsedState;
    if (typeof storedState === 'string') {
      parsedState = JSON.parse(storedState);
    } else {
      parsedState = storedState;
    }
    console.log('parsedState', parsedState, storedState);
    var obj = parsedState?.cart;
    var objLength = obj?.length || 0;
    if (objLength) {
      for (var key = objLength - 1; key >= 0; key--) {
        var expired = Date.parse(obj[key]["expires"]);
        var itemLocale = obj[key]['locale'];
        // Use options.locale instead of window.locale
        const currentLocale = (window as any).locale || locale || options.locale;
        if (hasExpired(now, expired)) {
          obj.splice(key, 1);
        } else if (itemLocale && currentLocale && itemLocale.toLowerCase().replace("_", "-") !== currentLocale.toLowerCase().replace("_", "-")) {
          obj.splice(key, 1);
        }
      }
    }

    // Update cookie with cleaned cart state if items were removed BEFORE restoring
    if (obj && obj.length !== parsedState.cart.length) {
      parsedState.cart = obj;
      StorageUtils.setCookie(cartKey, JSON.stringify(parsedState));
      console.log('✅ Cookie updated with cleaned cart state');
    }

    // Restore cart state from cookie to Redux store
    // This ensures the state is restored on page refresh
    // Use the cleaned cart array (after removing expired items)
    // IMPORTANT: Preserve lastAction to prevent cookie value reset
    if (parsedState && obj) {
      nvstore.dispatch({
        type: actionTypes.SET_CART,
        cart: obj,
        lastAction: null
      });
      console.log('✅ Cart state restored from cookie:', obj.length, 'items');
    } else {
      // If no cart in cookie, initialize empty cart
      nvstore.dispatch({
        type: actionTypes.SET_CART,
        cart: [],
        lastAction: null
      });
      console.log('✅ Cart initialized as empty (no cookie data)');
    }

    // Always call setProducts to scan page for products, regardless of cart state
    // Pass the already-restored cart state to prevent overwriting
    ProductModule.setProducts(cartKey, options, Array.from(cartEnabledLocales), isHeadless);
    // Open / close cart
    function closeSearchDropdowns() {
      const searchAbortContainer = DOMUtils.querySelector(".search-abort-container");
      console.log(searchAbortContainer?.classList.contains("hide"));

      if (searchAbortContainer && !searchAbortContainer.classList.contains("hide")) {
        const searchAbortIcon = DOMUtils.querySelector("a.search-abort-icon") as HTMLElement;
        if (searchAbortIcon) {
          searchAbortIcon.style.display = searchAbortIcon.style.display === 'none' ? '' : 'none';
        }

        if (searchAbortContainer.className.indexOf("hide") > 0) {
          searchAbortContainer.classList.remove("hide");

          const searchWhite = DOMUtils.querySelector(".search_white");
          if (searchWhite) searchWhite.classList.add("search-shown");

          const searchOutterContainer = DOMUtils.querySelector("div.search_white > div.search-outter-container");
          if (searchOutterContainer) searchOutterContainer.classList.remove("hide-wid-0");
        } else {
          const searchWhite = DOMUtils.querySelector(".search_white");
          if (searchWhite) searchWhite.classList.remove("search-shown");

          searchAbortContainer.classList.add("hide");

          const navSearchIcon = DOMUtils.querySelector("svg.nav-search-icon") as HTMLElement | SVGElement;
          if (navSearchIcon) (navSearchIcon as HTMLElement).style.display = "inline-block";

          const searchOutterContainer = DOMUtils.querySelector("div.search_white > div.search-outter-container");
          if (searchOutterContainer) searchOutterContainer.classList.add("hide-wid-0");
        }
      }

      const searchOverlay = DOMUtils.querySelector(".searchoverlay");
      if (searchOverlay && searchOverlay.classList.contains("searchoverlaydisplay")) {
        searchOverlay.classList.remove("searchoverlaydisplay");
      }
    }

    const cartElement = getCartElement();

    // Explicitly hide cart on initialization to ensure it's not visible on page load
    if (cartElement) {
      cartElement.classList.remove("cart--shown");
      console.log('🔒 Cart hidden on initialization');
    }

    const navCartLink = DOMUtils.querySelector(".nav-cart-link");
    if (navCartLink) {
      navCartLink.setAttribute("aria-expanded", "false");
    }

    // Cart close button handlers
    const cartCloseButtons = DOMUtils.querySelectorAll(".cart__close, .js-cart-close, .cart__continue-shopping");
    cartCloseButtons.forEach(button => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        // Hide all cart elements (both desktop and mobile)
        applyToAllCarts((cart) => {
          cart.classList.remove("cart--shown");
        });

        const navCartLink = DOMUtils.querySelector(".nav-cart-link");
        if (navCartLink) navCartLink.setAttribute("aria-expanded", "false");
        return false;
      });
    });

    // Cart toggle handlers (click on cart icon to show/hide)
    const cartToggleButtons = DOMUtils.querySelectorAll(".nav-cart-link, .cart-counter");
    console.log('🔧 Setting up cart toggle handlers. Found buttons:', cartToggleButtons.length);
    console.log('🔧 Cart element found:', !!cartElement);

    cartToggleButtons.forEach(button => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation(); // Prevent other handlers from firing
        console.log('🖱️ Cart icon clicked!', e.target);
        // closeSearchDropdowns();

        // Get cart element fresh each time to ensure we have the latest reference
        const currentCartElement = getCartElement();
        const navCartLink = document.querySelector(".nav-cart-link");

        // Check if cart is currently shown
        const isCartShown = currentCartElement && currentCartElement.classList.contains("cart--shown");
        console.log('🔍 Cart currently shown?', isCartShown);
        console.log('🔍 Cart element found?', !!currentCartElement);

        if (isCartShown) {
          // Hide cart - apply to all cart elements to ensure both desktop and mobile are hidden
          applyToAllCarts((cart) => {
            cart.classList.remove("cart--shown");
          });
          console.log('🛒 Cart hidden (toggle)');
          if (navCartLink) navCartLink.setAttribute("aria-expanded", "false");
        } else {
          // Show cart - apply to the currently visible cart element
          if (currentCartElement) {
            currentCartElement.classList.add("cart--shown");
            console.log('✅ Added cart--shown class to:', currentCartElement);
            console.log('📍 Viewport width:', window.innerWidth, 'isMobile:', window.innerWidth <= 1023);
            if (navCartLink) navCartLink.setAttribute("aria-expanded", "true");
            console.log('🛒 Cart shown (toggle)');
          } else {
            console.error('❌ Cart element not found! Available carts:', getAllCartElements().length);
            // Fallback: try to apply to all carts if specific one not found
            applyToAllCarts((cart) => {
              cart.classList.add("cart--shown");
            });
          }
        }
        return false;
      }, true); // Use capture phase to catch event early
    });

    // Window click to close cart when clicking outside (matches legacy jQuery behavior)
    // Use capture: false (bubble phase) so toggle handlers can stop propagation first
    window.addEventListener("click", (event) => {
      const target = event.target as HTMLElement;

      // Don't close if clicking on the close button itself (handled by document-level handler)
      const isCloseButton = target.closest(".cart__close, .js-cart-close, .cart__continue-shopping");
      if (isCloseButton) {
        return; // Let the document-level handler deal with it
      }

      // Check if click is OUTSIDE the cart-main-section
      if (!target.closest(".cart-main-section")) {
        // Don't close if clicking on cart icon or cart counter (they handle their own toggle)
        // Check both the target and if it's inside these elements
        const clickedCartToggle = target.closest(".nav-cart-link") ||
          target.closest(".cart-counter") ||
          target.closest(".nvcarticon") ||
          target.classList.contains("nav-cart-link") ||
          target.classList.contains("cart-counter") ||
          target.classList.contains("nvcarticon");

        if (clickedCartToggle) {
          // Event was handled by toggle handler, don't interfere
          return;
        }

        // Close cart - user clicked outside (hide all carts)
        applyToAllCarts((cart) => {
          cart.classList.remove("cart--shown");
        });

        const cartBlocker = document.querySelector(".cart-blocker");
        if (cartBlocker) cartBlocker.classList.remove("cart-blocker--shown");

        const navCartLink = document.querySelector(".nav-cart-link");
        if (navCartLink) navCartLink.setAttribute("aria-expanded", "false");
      }
    }, false); // Explicitly use bubble phase

    // NV cart icon click handler
    const nvCartIcons = document.querySelectorAll(".nvcarticon");
    nvCartIcons.forEach(icon => {
      icon.addEventListener("click", (e) => {
        e.preventDefault();

        const megaOverlay = document.querySelector(".mega-overlay");
        if (megaOverlay && !megaOverlay.classList.contains("hide")) {
          (megaOverlay as HTMLElement).click();
        }

        const navOverlay = document.querySelector(".navoverlay");
        const nvMenu = document.querySelector(".nv-menu");
        if (navOverlay && nvMenu &&
          navOverlay.classList.contains("navoverlay-display") &&
          nvMenu.classList.contains("menuopen")) {
          (navOverlay as HTMLElement).click();
        }

        if (cartElement) cartElement.classList.add("cart--shown");

        const navCartLink = document.querySelector(".nav-cart-link");
        if (navCartLink) navCartLink.setAttribute("aria-expanded", "true");

        console.log("---nav-cart-link--->");
        return false;
      });
    });

    // Bind user interaction events for cart items using document-level event delegation
    // Using capture phase (true) to ensure we catch events before other handlers
    document.addEventListener("click", (event) => {
      const target = event.target as HTMLElement;

      // PRIORITY 1: Checkout handler - check this FIRST before any scope filtering
      const checkoutButton = target.closest(".js-checkout");
      if (checkoutButton) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation(); // Stop ALL other handlers
        console.log('💳 Checkout button clicked - processing...');
        document.body.classList.add("loading");

        const state = nvstore.getState();
        const cart = state.cart.filter((item: any) => item.category === 'nvidia');
        const partnerCart = state.cart.filter((item: any) => item.category === 'partner');
        const activeTabCategory = state['activeTab'] ? state['activeTab'] : sessionStorage.getItem('category');

        console.log('Checkout==>', activeTabCategory, state.activeTab);

        if (activeTabCategory === 'nvidia') {
          processCart(cart, 'nvidia', partnerCart, state);
        } else {
          processCart(partnerCart, 'partner', partnerCart, state);
        }
        return;
      }

      // PRIORITY 2: Cart close button handler - check BEFORE scope filtering
      // This needs to work for dynamically rendered close buttons
      // Check if click is on close button (handles pseudo-elements ::before/::after via closest)
      // The close button uses CSS pseudo-elements, so clicks might be on the element itself
      let closeButton: HTMLElement | null = target.closest(".cart__close, .js-cart-close, .cart__continue-shopping") as HTMLElement;

      // Also check if target itself is the close button
      if (!closeButton && target.classList) {
        if (target.classList.contains("cart__close") ||
          target.classList.contains("js-cart-close") ||
          target.classList.contains("cart__continue-shopping")) {
          closeButton = target;
        }
      }

      // Also check parent element in case click is on a child
      if (!closeButton && target.parentElement && target.parentElement.classList) {
        if (target.parentElement.classList.contains("cart__close") ||
          target.parentElement.classList.contains("js-cart-close") ||
          target.parentElement.classList.contains("cart__continue-shopping")) {
          closeButton = target.parentElement as HTMLElement;
        }
      }

      if (closeButton) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        console.log('❌ Close button clicked', {
          target: target,
          targetClass: target.className,
          targetTag: target.tagName,
          closeButton: closeButton,
          closeButtonClass: closeButton.className,
          cartsFound: getAllCartElements().length
        });

        // Hide all cart elements (both desktop and mobile)
        const allCarts = getAllCartElements();
        console.log('🔍 Found carts to close:', allCarts.length);
        allCarts.forEach((cart, index) => {
          console.log(`🔍 Closing cart ${index + 1}:`, cart.className, 'has cart--shown:', cart.classList.contains("cart--shown"));
          cart.classList.remove("cart--shown");
        });

        const cartBlocker = document.querySelector(".cart-blocker");
        if (cartBlocker) cartBlocker.classList.remove("cart-blocker--shown");

        const navCartLink = document.querySelector(".nav-cart-link");
        if (navCartLink) navCartLink.setAttribute("aria-expanded", "false");

        console.log('✅ Close button handler completed');
        return;
      }

      // Only handle other cart events from within cart containers
      // Check if target is inside any cart container (works for both desktop and mobile)
      const cartContainer = target.closest("#cart") ||
        target.closest(".nv__cart__items") ||
        target.closest(".cart-main-section");

      if (!cartContainer) {
        return;
      }

      // Debug: Log which nav the click came from
      const navParent = cartContainer.closest('.desktop-nav, .mobile-nav');
      console.log('📍 Cart action from:', navParent?.className || 'unknown nav');

      // Remove item handler
      if (target.closest(".js-cart-item-remove")) {
        event.preventDefault();
        event.stopPropagation();
        const button = target.closest(".js-cart-item-remove") as HTMLElement;
        // Check both data attributes to support different product types
        const productId = button.dataset.marketplaceId ||
          button.dataset.productId;
        console.log('🗑️ Remove item clicked:', productId, 'from:', button.closest('.desktop-nav, .mobile-nav')?.className);
        if (productId) {
          nvstore.dispatch({
            type: actionTypes.REMOVE_ITEM_FROM_CART,
            id: productId,
          });
        }
        return;
      }

      // Increment quantity handler
      if (target.closest(".js-cart-item-inc")) {
        event.preventDefault();
        event.stopPropagation();
        const button = target.closest(".js-cart-item-inc") as HTMLElement;
        // Use marketplaceId as that's what the template uses (data-marketplace-id)
        const productId = button.dataset.marketplaceId || button.dataset.productId;
        console.log('➕ Increment clicked:', productId, 'from:', button.closest('.desktop-nav, .mobile-nav')?.className);

        if (productId) {
          const state = nvstore.getState();
          const product = state.cart.find((item: any) => item.id === productId || item.id == productId);
          const category = product?.category || 'nvidia';

          nvstore.dispatch({
            type: actionTypes.INC_ITEM_QUANTITY,
            id: productId,
            category: category,
          });
        }
        return;
      }

      // Decrement quantity handler
      if (target.closest(".js-cart-item-dec")) {
        event.preventDefault();
        event.stopPropagation();
        const button = target.closest(".js-cart-item-dec") as HTMLElement;
        // Use marketplaceId as that's what the template uses (data-marketplace-id)
        const productId = button.dataset.marketplaceId || button.dataset.productId;
        console.log('➖ Decrement clicked:', productId, 'from:', button.closest('.desktop-nav, .mobile-nav')?.className);

        if (productId) {
          const state = nvstore.getState();
          const product = state.cart.find((item: any) => item.id === productId || item.id == productId);
          const category = product?.category || 'nvidia';

          nvstore.dispatch({
            type: actionTypes.DEC_ITEM_QUANTITY,
            id: productId,
            category: category,
          });
        }
        return;
      }
    }, true); // Use capture phase to catch events early

    // Additional fallback: Direct listener for checkout button clicks
    // This ensures the checkout button works even if other handlers interfere
    document.addEventListener("click", (event) => {
      const target = event.target as HTMLElement;
      const checkoutBtn = target.closest(".js-checkout");

      if (checkoutBtn) {
        console.log('🛒 FALLBACK: Checkout button detected via direct listener');
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        // Double-check if already being processed
        if (document.body.classList.contains("loading")) {
          console.log('⚠️ Already processing checkout, skipping...');
          return;
        }

        document.body.classList.add("loading");

        const state = nvstore.getState();
        const cart = state.cart.filter((item: any) => item.category === 'nvidia');
        const partnerCart = state.cart.filter((item: any) => item.category === 'partner');
        const activeTabCategory = state['activeTab'] ? state['activeTab'] : sessionStorage.getItem('category');

        console.log('Checkout (fallback)==>', activeTabCategory, state.activeTab);

        if (activeTabCategory === 'nvidia') {
          processCart(cart, 'nvidia', partnerCart, state);
        } else {
          processCart(partnerCart, 'partner', partnerCart, state);
        }
      }
    }, false); // Bubble phase as fallback

    // Product add button handler using event delegation
    const productItems = document.querySelectorAll(".js-product-item");
    productItems.forEach(item => {
      item.addEventListener("click", (event) => {
        const target = event.target as HTMLElement;

        if (target.closest(".js-add-button")) {
          event.preventDefault();
          event.stopPropagation(); // Prevent event from bubbling to window click handler
          let quantity = 1;
          let productId = null;
          const cartData: any[] = [];
          const componentRoot = event.currentTarget as HTMLElement;

          productId = componentRoot.dataset.bigCommId;

          if (productId) {
            quantity = 1;
            nvstore.dispatch({
              type: actionTypes.ADD_ITEM_TO_CART,
              id: productId,
              quantity: quantity,
              category: 'partner'
            });
            sessionStorage.setItem('category', 'partner');
          } else {
            quantity = 1;
            productId = componentRoot.dataset.marketPlaceId;
            const cartItems = {
              "productId": componentRoot.dataset.marketPlaceId,
              "quantity": 1
            };
            cartData.push(cartItems);

            nvstore.dispatch({
              type: actionTypes.ADD_ITEM_TO_CART,
              id: productId,
              quantity: quantity,
              category: 'nvidia'
            });
            sessionStorage.setItem('category', 'nvidia');
          }
        }
      });
    });

    // ============ end of event binding
  };
};